<template>
	<div class="p4">
		<!-- Loop through config array and render components -->
		<div v-for="(item, i) in config" :key="i">
			<div v-if="item.componentName === 'j-base-select'">
				<core-form-input
					:componentName="item.componentName"
					:id="item.id"
					:size="size"
					:fsLabel="fsLabel"
					:name="item.id"
					:options="item.options"
					:description="item.description"
					:placeholder="item.placeholder"
					:label="item.label"
					:subheadline="item.subheadline"
					v-validate="item.validate"
					v-model="form[item.id]"
					:validationErrors="errors"
					class="mb6"
				/>
			</div>
			<div v-else-if="item.showFieldWhen">
				<core-form-input
					:componentName="item.componentName"
					v-if="
						form[item.showFieldWhen.targetId] === item.showFieldWhen.targetValue
					"
					:id="item.id"
					:size="size"
					:fsLabel="fsLabel"
					:name="item.id"
					:options="item.options"
					:description="item.description"
					:placeholder="item.placeholder"
					:label="item.label"
					:subheadline="item.subheadline"
					v-validate="item.validate"
					v-model="form[item.id]"
					:validationErrors="errors"
					class="mb6"
				/>
			</div>
			<div v-else>
				<core-form-input
					:componentName="item.componentName"
					:id="item.id"
					:size="size"
					:fsLabel="fsLabel"
					:name="item.id"
					:options="item.options"
					:description="item.description"
					:placeholder="item.placeholder"
					:label="item.label"
					:subheadline="item.subheadline"
					v-validate="item.validate"
					v-model="form[item.id]"
					:validationErrors="errors"
					class="mb6"
				/>
			</div>
		</div>

		<div class="flex xs-12 p1">
			<j-button @click="handleAdd" :isLoading="isLoading">{{
				$t("add")
			}}</j-button>
		</div>
	</div>
</template>

<script>
import CoreFormInput from "@/modules/core/components/CoreFormInput.vue"
import JButton from "@/components/JButton/"
import api from "@/modules/core/api/"
import { transformDate } from "@/utils/"

export default {
	props: {
		/** id of entity */
		id: {
			type: String,
			required: true
		},

		/** used to identify contacts, identities, addresses, facts */
		for: {
			type: String,
			required: true
		},

		/**
		 * Array of objects, each object represents input field
		 *
		 * Possible keys:
		 *
		 * componentName: - defines which component CoreFormInput will render
		 * id: - define id to use for this input
		 * label: - define label to display with input
		 * subheadline: - subheadline prop for CoreFormInput
		 * options: - option prop for CoreFormInput
		 * description - description prop for CoreFormInput
		 * placeholder - placeholder prop for CoreFormInput
		 * validate - v-validate prop for CoreFormInput
		 * showFieldWhen : {targetValue: value , targetId: option } - renders as <div v-if="option === value"/>
		 *
		 */
		config: {
			type: Array,
			required: true
		},

		/*
		 * Size of input component
		 */
		size: { type: String, required: false, default: "medium" },

		/*
		 * Font size of labels
		 */
		fsLabel: { type: String, default: "fs4" }
	},

	components: {
		CoreFormInput,
		JButton
	},

	data() {
		return {
			isLoading: false,
			form: {}
		}
	},

	methods: {
		async handleAdd() {
			// Validate form
			const isValid = await this.$validator.validate()
			if (!isValid) return

			// Validation for JInputObjectList
			if (
				this.config
					.map(item => item.componentName)
					.includes("j-input-object-list")
			) {
				const isObjectValid = await this.validateInputObjectList()
				if (!isObjectValid) return
			}

			let apiKeys = null
			let input = null

			let entityId = this.id

			// For Contacts
			if (this.for === "contacts") {
				apiKeys = "addContact"

				let type =
					this.form.selectType !== "Others"
						? this.form.selectType
						: this.form.otherType

				let value = this.form.value

				input = { contactInput: { type, value } }
			}

			// For Identities
			if (this.for === "identities") {
				apiKeys = "addIdentity"

				let type =
					this.form.selectType !== "Others"
						? this.form.selectType
						: this.form.otherType

				let value = this.form.value

				input = { addIdentityInput: { type, value } }
			}

			// For Addresses
			if (this.for === "addresses") {
				let fields = this.form.fields

				apiKeys = "addAddress"
				input = {
					addressInput: {
						type: this.form.type,
						fields: fields
					}
				}
			}

			// For Facts
			if (this.for === "facts") {
				let value = null

				if (this.form.factOptions === "boolean")
					value = { booleanValue: this.form.booleanValue }

				if (this.form.factOptions === "dateValue")
					value = {
						dateValue: transformDate(this.form.dateValue)
					}
				else
					value = {
						[this.form.factOptions]: this.form[this.form.factOptions]
					}

				apiKeys = "addFact"
				input = {
					factInput: {
						type: this.form.type,
						value: value
					}
				}
			}

			this.isLoading = true

			const res = await api[apiKeys]({
				entityId,
				...input
			})

			this.isLoading = false

			// Error
			if (res.errors) {
				this.$store.dispatch("showError", res.errors)
			}

			this.$emit("added")
		},

		validateInputObjectList() {
			let hasMissingKeyOrValue =
				this.form.fields !== null
					? this.form.fields
							.map(item => {
								return !item.type || !item.value ? false : true
							})
							.includes(false)
					: true

			let array = this.form.fields.map(item => item.type)
			let hasDuplicateKey = array.some(item => {
				return array.indexOf(item) !== array.lastIndexOf(item)
			})

			return !hasMissingKeyOrValue && !hasDuplicateKey ? true : false
		}
	},

	created() {
		if (this.for === "facts") this.$set(this.form, "booleanValue", false)
	}
}
</script>
